import { Platform } from 'react-native';
import { decode } from 'base64-arraybuffer';
import { supabase } from './supabase';
import { storageObjectPath } from './storageObjectPath';
import type { DocumentPickerAsset } from 'expo-document-picker';

export const DOCUMENT_MIME_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
export const DOCUMENT_MAX_BYTES = 50 * 1024 * 1024;
const extensions = ['pdf', 'doc', 'docx'];

export function validateDocumentFile(file: Pick<DocumentPickerAsset, 'name' | 'size' | 'mimeType'>): string {
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  const index = extensions.indexOf(extension);
  if (index < 0 || (file.mimeType && file.mimeType !== 'application/octet-stream' && file.mimeType !== DOCUMENT_MIME_TYPES[index])) {
    throw new Error('workflow.invalidFile');
  }
  if (file.size !== undefined && (!Number.isFinite(file.size) || file.size <= 0 || file.size > DOCUMENT_MAX_BYTES)) {
    throw new Error('workflow.invalidSize');
  }
  return DOCUMENT_MIME_TYPES[index];
}

export async function uploadPickedDocument(file: DocumentPickerAsset, userId: string, id: string) {
  const mimeType = validateDocumentFile(file);
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError || !user || user.id !== userId) throw new Error('workflow.authRequired');
  let bytes: ArrayBuffer;
  if (Platform.OS === 'web') {
    if (file.file) bytes = await file.file.arrayBuffer();
    else {
      const response = await fetch(file.uri);
      if (!response.ok) throw new Error('workflow.uploadFailed');
      bytes = await response.arrayBuffer();
    }
  } else {
    const fs = await import('expo-file-system/legacy');
    const info = await fs.getInfoAsync(file.uri);
    if (!info.exists || info.isDirectory || info.size <= 0 || info.size > DOCUMENT_MAX_BYTES) throw new Error('workflow.invalidSize');
    bytes = decode(await fs.readAsStringAsync(file.uri, { encoding: fs.EncodingType.Base64 }));
  }
  validateDocumentFile({ ...file, size: bytes.byteLength });
  const path = `${userId}/${id}.${extensions[DOCUMENT_MIME_TYPES.indexOf(mimeType)]}`;
  const { error } = await supabase.storage.from('documents').upload(path, bytes, { contentType: mimeType, upsert: false });
  if (error) throw new Error('workflow.uploadFailed');
  return { path, file_path: `storage://documents/${path}`, file_size: bytes.byteLength, mime_type: mimeType };
}

export async function resolveDocumentUrl(value: string): Promise<string> {
  let path: string;
  if (value.startsWith('storage://documents/')) {
    path = value.slice('storage://documents/'.length);
    if (!path || path.split('/').some(part => !part || part === '.' || part === '..' || /[\\\u0000-\u001f]/.test(part))) throw new Error('workflow.fileUnavailable');
  } else {
    const url = new URL(value);
    if (url.protocol !== 'https:') throw new Error('workflow.fileUnavailable');
    const base = supabase.storage.from('documents').getPublicUrl('').data.publicUrl;
    // Preserve legacy external HTTPS attachments, but never make private storage public.
    if (url.origin !== new URL(base).origin) return url.toString();
    path = storageObjectPath(value, 'documents', base);
  }
  const { data, error } = await supabase.storage.from('documents').createSignedUrl(path, 300);
  if (error || !data?.signedUrl) throw new Error('workflow.fileUnavailable');
  return data.signedUrl;
}

export async function presentDocumentFile(record: { id: string; title: string; file_path: string; mime_type?: string | null }, action: 'download' | 'share') {
  const url = await resolveDocumentUrl(record.file_path);
  if (Platform.OS === 'web') {
    const response = await fetch(url);
    if (!response.ok) throw new Error('workflow.fileUnavailable');
    const blob = await response.blob();
    const extension = extensions[DOCUMENT_MIME_TYPES.indexOf(record.mime_type || '')] || 'bin';
    const name = `${record.title.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_')}.${extension}`;
    const file = new File([blob], name, { type: blob.type });
    if (action === 'share' && navigator.canShare?.({ files: [file] })) {
      try { await navigator.share({ files: [file], title: record.title }); }
      catch (error) { if ((error as Error).name !== 'AbortError') throw error; }
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl; anchor.download = name;
    document.body.appendChild(anchor); anchor.click(); anchor.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    return;
  }
  const fs = await import('expo-file-system/legacy');
  const sharing = await import('expo-sharing');
  if (!fs.cacheDirectory || !await sharing.isAvailableAsync()) throw new Error('workflow.fileUnavailable');
  const extension = new URL(url).pathname.match(/\.(pdf|docx?|png|jpe?g|xlsx?|txt)$/i)?.[0] || '.bin';
  const destination = `${fs.cacheDirectory}document-${record.id.replace(/[^a-zA-Z0-9-]/g, '_')}${extension}`;
  try {
    const file = await fs.downloadAsync(url, destination);
    if (file.status !== 200) throw new Error('workflow.fileUnavailable');
    await sharing.shareAsync(file.uri, { mimeType: record.mime_type || 'application/octet-stream', dialogTitle: record.title });
  } finally {
    await fs.deleteAsync(destination, { idempotent: true }).catch(() => undefined);
  }
}
